import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import '../Components/modal.css';
import './AddUser.css';
import './edit.css';
import axios from 'axios';
import { BASE_URL_API } from '../Constants';
import Select from 'react-select';
import { useAuth } from '../hooks/AuthProvider';
import Alert from '../Components/Alert';

import { LanguageContext } from '../context/LanguageContext';
import userTranslations from '../languages/user.json';
import { getErrorMessage } from '../utils/errorUtils';
import { countries } from '../lib/countries';
import { normalizeOrganizationListRow } from '../utils/listRowIds';

import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

const InviteIcon = () => (
  <svg
    className="adduser-icon adduser-icon--invite"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5 7L11.293 11.293C11.6835 11.6835 12.3165 11.6835 12.707 11.293L19 7"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NameIcon = () => (
  <svg
    className="adduser-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M6 20C6.62132 18.0503 8.49487 16.75 10.5556 16.75H13.4444C15.5051 16.75 17.3787 18.0503 18 20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const EmailIcon = () => (
  <svg
    className="adduser-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 7L11.293 11.293C11.6835 11.6835 12.3165 11.6835 12.707 11.293L19 7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RoleIcon = () => (
  <svg
    className="adduser-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5 20C5.8 17.6667 8.13333 16 12 16C15.8667 16 18.2 17.6667 19 20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const OrgIcon = () => (
  <svg
    className="adduser-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="3" y="14" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="13" y="14" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const defaultCountry =
  countries.find((c) => c.name === 'United States') || { name: 'United States', code: '+1', position: '' };
const EXCLUDED_ROLES = ['Owner', 'Super'];

const getCurrentCountry = (countryName) => {
  return countries.find((c) => c.name === countryName) || defaultCountry;
};

const getCountryFromPhoneInput = (phoneValue, preferredCountryName) => {
  const normalizedPhone = String(phoneValue || '').replace(/[^\d+]/g, '');

  if (!normalizedPhone.startsWith('+')) {
    return null;
  }

  const matches = countries.filter((country) => normalizedPhone.startsWith(country.code));
  if (matches.length === 0) {
    return null;
  }

  const sortedMatches = [...matches].sort((a, b) => b.code.length - a.code.length);
  const preferredMatch = sortedMatches.find((country) => country.name === preferredCountryName);
  if (preferredMatch) {
    return preferredMatch;
  }

  const defaultMatch = sortedMatches.find((country) => country.name === defaultCountry.name);
  return defaultMatch || sortedMatches[0];
};

const getCountryFlagStyle = (country) => {
  if (country?.flagImage) {
    return {
      backgroundImage: `url(${country.flagImage})`,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    };
  }

  return {
    backgroundPosition: country?.position || '0px 0px',
  };
};

export const AddUser = ({ open, setOpen, refresh }) => {
  const { org, permissions } = useAuth();
  const { lang } = useContext(LanguageContext);

  const canInviteUsers = permissions['invite_users'];

  const t = userTranslations[lang].addUser;
  const isRtl = ['ar', 'ur'].includes(lang);

  const [rows, setRows] = useState([
    {
      email: '',
      name: '',
      organization_id: org.id,
      organization_name: org.name,
      phone: defaultCountry.code,
      country: defaultCountry.name,
      role: 'User',
    },
  ]);
  const [organizations, setOrganizations] = useState([]);
  const [allRoles, setAllRoles] = useState(['User']);
  const [openCountryIndex, setOpenCountryIndex] = useState(null);
  const countryMenuRefs = useRef({});

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right',
  });

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? 'left' : 'right',
      });
    },
    [setAlertState, isRtl]
  );

  const fetchRoles = useCallback(async () => {
    if (!canInviteUsers) return;

    try {
      const res = await axios.get(`${BASE_URL_API}/roles`);

      const allRolesFromDB = Array.isArray(res.data?.data) ? res.data.data.map((role) => role.name) : [];
      const filteredRoleNames = allRolesFromDB.filter((name) => !EXCLUDED_ROLES.includes(name));

      const safeRoles = filteredRoleNames.length > 0 ? filteredRoleNames : ['User'];

      const defaultRoleForNewRow = safeRoles.includes('User') ? 'User' : safeRoles[safeRoles.length - 1];

      setAllRoles(safeRoles);

      setRows((prevRows) => {
        if (!Array.isArray(prevRows) || prevRows.length === 0) return prevRows;
        const currentRole = prevRows[0]?.role || 'User';

        if (safeRoles.includes(currentRole)) return prevRows;

        return prevRows.map((row) => ({
          ...row,
          role: defaultRoleForNewRow,
        }));
      });
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      const errorMessage = getErrorMessage(err);
      showAlert('error', `Failed to load roles: ${errorMessage}`);

      const fallbackRoles = ['Admin', 'Staff', 'User'].filter((name) => !EXCLUDED_ROLES.includes(name));
      const safeFallback = fallbackRoles.length > 0 ? fallbackRoles : ['User'];
      const fallbackDefault = safeFallback.includes('User') ? 'User' : safeFallback[safeFallback.length - 1];

      setAllRoles(safeFallback);
      setRows((prevRows) => {
        if (!Array.isArray(prevRows) || prevRows.length === 0) return prevRows;
        const currentRole = prevRows[0]?.role || 'User';
        if (safeFallback.includes(currentRole)) return prevRows;

        return prevRows.map((row) => ({
          ...row,
          role: fallbackDefault,
        }));
      });
    }
  }, [canInviteUsers, showAlert]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    if (!canInviteUsers) return;

    const fetchOrganizations = async () => {
      try {
        const res = await axios.get(`${BASE_URL_API}/organization/list`);
        if (res.data && Array.isArray(res.data.data)) {
          const normalizedOrganizations = res.data.data.map(normalizeOrganizationListRow);
          setOrganizations(normalizedOrganizations);
          setRows((prevRows) => {
            const currentOrgId = prevRows[0]?.organization_id;
            const currentOrgName = prevRows[0]?.organization_name;

            const authOrgInList = normalizedOrganizations.find((o) => o.id === org.id);

            if (!authOrgInList || currentOrgId !== org.id || currentOrgName !== org.name) {
              return prevRows.map((row, idx) =>
                idx === 0
                  ? {
                      ...row,
                      organization_id: authOrgInList ? authOrgInList.id : org.id || null,
                      organization_name: authOrgInList ? authOrgInList.name : org.name || 'Main',
                    }
                  : row
              );
            }
            return prevRows;
          });
        } else {
          console.error('Received unexpected data format from /organization/list:', res.data);
          setOrganizations([]);
          showAlert('error', 'Received an invalid format from the server.');
        }
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        showAlert('error', `Failed to load organizations: ${errorMessage}`);
        setOrganizations([]);
      }
    };

    fetchOrganizations();
  }, [canInviteUsers, org.id, org.name, showAlert]);

  useEffect(() => {
    if (openCountryIndex === null || openCountryIndex < rows.length) return;
    setOpenCountryIndex(null);
  }, [openCountryIndex, rows.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openCountryIndex === null) return;

      const activeMenu = countryMenuRefs.current[openCountryIndex];
      if (activeMenu && !activeMenu.contains(event.target)) {
        setOpenCountryIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCountryIndex]);

  const handleDeleteRow = (index) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
    setOpenCountryIndex((prevIndex) => {
      if (prevIndex === null) return prevIndex;
      if (prevIndex === index) return null;
      return prevIndex > index ? prevIndex - 1 : prevIndex;
    });
  };

  const addUser = async () => {
    const invalidRow = rows.find((row) => !row.email || !row.name);
    if (invalidRow) {
      showAlert('error', 'All users must have a valid Name and Email.');
      return;
    }

    try {
      const payload = rows.map((row) => ({
        ...row,
        password: '',
      }));

      const res = await axios.post(`${BASE_URL_API}/user/create`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (res.status === 201 && res.data.status === 'OK') {
        showAlert('success', `${res.data.data.length} user(s) have been invited successfully.`);
        refresh();
        setTimeout(() => setOpen(false), 2000);
      } else {
        throw new Error(res.data?.message || 'Received an unexpected response from the server.');
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      showAlert('error', errorMessage);
    }
  };

  const selectCountry = useCallback((idx) => {
    setOpenCountryIndex((currentIndex) => (currentIndex === idx ? null : idx));
  }, []);

  const selectCountryItem = useCallback(
    (e, idx, country) => {
      e.stopPropagation();
      if (!country?.name || !country?.code) return;

      setRows((prevRows) =>
        prevRows.map((row, i) =>
          i === idx
            ? {
                ...row,
                country: country.name,
                phone: country.code,
              }
            : row
        )
      );
      setOpenCountryIndex(null);
    },
    [setRows]
  );

  const handlePhoneChange = useCallback(
    (idx, phoneValue) => {
      setRows((prevRows) =>
        prevRows.map((row, i) => {
          if (i !== idx) return row;

          const matchedCountry = getCountryFromPhoneInput(phoneValue, row.country);
          return {
            ...row,
            phone: phoneValue,
            country: matchedCountry ? matchedCountry.name : row.country,
          };
        })
      );
    },
    [setRows]
  );

  if (!canInviteUsers) {
    return (
      <div id="modal">
        <div id="popup" className={`adduser-popup adduser-popup--denied ${isRtl ? 'rtl' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
          <div className="modal-header adduser-header">
            <h2 className="adduser-title">Permission Denied</h2>
          </div>
          <div className="modal-content adduser-content">
            <p className="adduser-denied-text">You do not have permission to invite new users.</p>
          </div>
          <div className="modal-footer adduser-footer">
            <button className="btn btn-cancel" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const organizationOptions = organizations.map((orgItem) => ({ value: orgItem.id, label: orgItem.name }));
  const roleOptions = allRoles.map((roleName) => ({ value: roleName, label: roleName }));

  return (
    <div id="modal">
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div id="popup" className={`adduser-popup ${isRtl ? 'rtl' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
        <div className="modal-header adduser-header">
          <div className="adduser-headerIconWrap">
            <InviteIcon />
          </div>

          <div className="adduser-headerText">
            <h2 className="adduser-title">{t.title || 'Invite User'}</h2>
            <p className="adduser-subtitle">{t.subtitle || 'Add a new user to the asset or workspace'}</p>
          </div>
        </div>

        <div className="modal-content adduser-content">
          <div className="adduser-sectionHead">
            <div className="adduser-sectionLabel">
              {t.sectionLabelUser || 'User'} ({rows.length})
            </div>

            <div className="adduser-tableHead">
              <span className="adduser-th">
                <NameIcon />
                {t.tableHeaders.name}
              </span>
              <span className="adduser-th">
                <EmailIcon />
                {t.tableHeaders.email}
              </span>
              <span className="adduser-th">
                <RoleIcon />
                {t.tableHeaders.role}
              </span>
              <span className="adduser-th">
                <OrgIcon />
                {t.tableHeaders.organization}
              </span>
            </div>
          </div>

          {rows.map((row, idx) => {
            const selectedOrgOption = organizationOptions.find((option) => option.value === row.organization_id);
            const selectedRoleOption = roleOptions.find((option) => option.value === row.role);
            const currentCountry = getCurrentCountry(row.country);

            return (
              <div key={idx} className="adduser-rowCard">
                <div className="adduser-rowGrid">
                  <div className="adduser-field">
                    <div className="adduser-nameWrap">
                      <input
                        type="text"
                        className="add-column adduser-input"
                        placeholder={t.tableHeaders.name}
                        value={row.name || ''}
                        maxLength={50}
                        onChange={(e) =>
                          setRows(rows.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)))
                        }
                      />
                      <span className="adduser-counter">{(row.name?.length || 0)}/50</span>
                    </div>
                  </div>

                  <div className="adduser-field">
                    <input
                      type="email"
                      className="add-column adduser-input"
                      placeholder={t.tableHeaders.email}
                      value={row.email || ''}
                      onChange={(e) => setRows(rows.map((r, i) => (i === idx ? { ...r, email: e.target.value } : r)))}
                    />
                  </div>

                  <div className="adduser-field">
                    <Select
                      id={`role-${idx}`}
                      options={roleOptions}
                      value={selectedRoleOption}
                      onChange={(selectedOption) =>
                        setRows((prevRows) =>
                          prevRows.map((r, i) =>
                            i === idx ? { ...r, role: selectedOption ? selectedOption.value : null } : r
                          )
                        )
                      }
                      classNamePrefix="adduser-select"
                      isClearable={false}
                    />
                  </div>

                  <div className="adduser-field">
                    <Select
                      id={`organization_name-${idx}`}
                      options={organizationOptions}
                      value={selectedOrgOption}
                      onChange={(selectedOption) => {
                        setRows((prevRows) =>
                          prevRows.map((r, i) =>
                            i === idx
                              ? {
                                  ...r,
                                  organization_id: selectedOption ? selectedOption.value : null,
                                  organization_name: selectedOption ? selectedOption.label : null,
                                }
                              : r
                          )
                        );
                      }}
                      classNamePrefix="adduser-select"
                      isClearable={false}
                    />
                  </div>

                  <div className="adduser-actions">
                    {rows.length > 1 && (
                      <IconButton
                        className="adduser-deleteBtn"
                        onClick={() => handleDeleteRow(idx)}
                        aria-label="delete"
                        size="small"
                      >
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </div>
                </div>

                <div className="adduser-phoneBlock">
                  <span className="adduser-phoneLabel">{t.tableHeaders.phone}</span>

                  <div className="adduser-phoneWrap">
                    <input
                      type="text"
                      className="add-column adduser-input adduser-phoneInput"
                      placeholder={t.tableHeaders.phone}
                      style={{ paddingInlineStart: '64px' }}
                      value={row.phone || ''}
                      onChange={(e) => {
                        handlePhoneChange(idx, e.target.value);
                      }}
                    />

                    <div
                      className="edit-select-country adduser-countrySelect"
                      title={t.selectCountryTitle}
                      ref={(element) => {
                        if (element) {
                          countryMenuRefs.current[idx] = element;
                        } else {
                          delete countryMenuRefs.current[idx];
                        }
                      }}
                      onClick={() => selectCountry(idx)}
                    >
                      <div className="edit-country-flag adduser-countryFlag" style={getCountryFlagStyle(currentCountry)} />
                      <span className={`edit-arrow ${openCountryIndex === idx ? 'selected' : ''}`} />
                      <ul
                        className={`edit-country-list ${openCountryIndex === idx ? 'selected' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {countries.map((country, countryIdx) => (
                          <li
                            className="edit-country-list-item"
                            key={`${country.name}-${country.code}-${countryIdx}`}
                            onClick={(e) => selectCountryItem(e, idx, country)}
                          >
                            <span className="edit-country-flag" style={getCountryFlagStyle(country)} />
                            <span className="edit-country-name">{country.name}</span>
                            <span className="edit-country-code">{country.code}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <button
            className="add-btn modal-add-btn adduser-addBtn"
            onClick={() => {
              const lastRow = rows[rows.length - 1];
              setOpenCountryIndex(null);
              setRows([
                ...rows,
                {
                  email: '',
                  name: '',
                  organization_name: lastRow?.organization_name || org.name || 'Main',
                  organization_id: lastRow?.organization_id || org.id || null,
                  role: lastRow?.role || (allRoles.length > 0 ? allRoles[allRoles.length - 1] : 'User'),
                  phone: lastRow?.phone || defaultCountry.code,
                  country: lastRow?.country || defaultCountry.name,
                },
              ]);
            }}
          >
            <span>+</span>
            <span>{t.buttonAdd}</span>
          </button>

          <span className="form-helper adduser-helper">
            {t.consentText.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </span>
        </div>

        <div className="modal-footer adduser-footer">
          <button className="btn btn-cancel" onClick={() => setOpen(!open)}>
            {t.buttonCancel}
          </button>
          <button className="btn btn-apply" onClick={addUser}>
            {t.buttonInvite}
          </button>
        </div>
      </div>
    </div>
  );
};
